import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { useMode } from "../context/ModeContext";
import {
    LuSun,
    LuMoon,
    LuHistory,
    LuHouse,
    LuCircleHelp,
    LuLogOut,
    LuUser,
    LuCrown,
    LuCreditCard,
    LuSettings,
    LuTarget,
    LuShield,
    LuScale,
    LuBitcoin,
    LuLandmark,
    LuArrowLeftRight,
    LuActivity,
    LuChartLine
} from "react-icons/lu";

function Navbar({ onHistoryClick, mode }) {
    const { isDark, toggleTheme } = useTheme();
    const { lang, toggleLanguage, t } = useLanguage();
    const { user, logout } = useAuth();
    const { mode: currentMode, setMode } = useMode();
    const location = useLocation();
    const navigate = useNavigate();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Use prop mode if provided, otherwise use context mode
    const activeMode = mode || currentMode;
    const basePath = activeMode === 'crypto' ? '/crypto' : '/fiat';

    const isActive = (path) => location.pathname === path || location.pathname === `${basePath}${path}`;

    const handleLogout = () => {
        logout();
        setDropdownOpen(false);
    };

    const handleDropdownClick = () => {
        setDropdownOpen(!dropdownOpen);
    };

    const handleMenuItemClick = (path) => {
        navigate(path);
        setDropdownOpen(false);
    };

    const handleModeSwitch = (newMode) => {
        setMode(newMode);
        const currentPath = location.pathname;

        // Extract the sub-path (e.g., /crypto/goals -> /goals)
        const subPath = currentPath.replace('/crypto', '').replace('/fiat', '') || '';

        // Navigate to the same page in the new mode
        navigate(`/${newMode}${subPath}`);
        setDropdownOpen(false);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const logoText = activeMode === 'crypto' ? 'Kripto' : activeMode === 'fiat' ? 'Fiat' : 'Getiri';

    return (
        <nav className={`navbar ${activeMode ? `navbar-${activeMode}` : ''}`}>
            <Link to={basePath || '/select-mode'} className="navbar-brand">
                <div className={`navbar-logo ${activeMode ? `logo-${activeMode}` : ''}`}>
                    {activeMode === 'crypto' ? <LuBitcoin /> : activeMode === 'fiat' ? <LuLandmark /> : 'G'}
                </div>
                <span className="navbar-title">{logoText}</span>
            </Link>

            <ul className="navbar-links">
                <li>
                    <Link to={basePath} className={location.pathname === basePath ? 'active' : ''}>
                        <LuHouse className="nav-icon" />
                        {t('nav.home')}
                    </Link>
                </li>
                <li>
                    <Link to={`${basePath}/market`} className={isActive('/market') ? 'active' : ''}>
                        {activeMode === 'crypto' ? <LuActivity className="nav-icon" /> : <LuChartLine className="nav-icon" />}
                        {lang === 'tr' ? 'Piyasa' : 'Market'}
                    </Link>
                </li>
                <li>
                    <Link to={`${basePath}/goals`} className={isActive('/goals') ? 'active' : ''}>
                        <LuTarget className="nav-icon" />
                        {t('nav.goals')}
                    </Link>
                </li>
                <li>
                    <Link to={`${basePath}/risk`} className={isActive('/risk') ? 'active' : ''}>
                        <LuShield className="nav-icon" />
                        {t('nav.risk')}
                    </Link>
                </li>
                <li>
                    <Link to={`${basePath}/compare`} className={isActive('/compare') ? 'active' : ''}>
                        <LuScale className="nav-icon" />
                        {t('nav.compare')}
                    </Link>
                </li>
                <li>
                    <Link to="/pricing" className={location.pathname === '/pricing' ? 'active' : ''}>
                        <LuCreditCard className="nav-icon" />
                        {t('nav.pricing')}
                    </Link>
                </li>
                <li>
                    <Link to="/support" className={location.pathname === '/support' ? 'active' : ''}>
                        <LuCircleHelp className="nav-icon" />
                        {t('nav.support')}
                    </Link>
                </li>
                {onHistoryClick && (
                    <li>
                        <button onClick={onHistoryClick}>
                            <LuHistory className="nav-icon" />
                            {t('nav.history')}
                        </button>
                    </li>
                )}
            </ul>

            <div className="navbar-right">
                {/* Profile Icon with Dropdown */}
                <div className="profile-dropdown" ref={dropdownRef}>
                    <button className="profile-icon-btn" onClick={handleDropdownClick}>
                        {user ? (
                            <div className="profile-avatar">
                                {user.name?.charAt(0).toUpperCase() || 'U'}
                            </div>
                        ) : (
                            <LuUser className="profile-icon" />
                        )}
                    </button>

                    {dropdownOpen && (
                        <div className="profile-dropdown-menu">
                            {user ? (
                                <>
                                    <div className="profile-dropdown-header">
                                        <div className="profile-dropdown-avatar">
                                            {user.name?.charAt(0).toUpperCase() || 'U'}
                                        </div>
                                        <div className="profile-dropdown-info">
                                            <span className="profile-dropdown-name">{user.name}</span>
                                            <span className="profile-dropdown-email">{user.email}</span>
                                        </div>
                                        {user.subscription?.plan === 'premium' && (
                                            <LuCrown className="premium-icon" />
                                        )}
                                    </div>
                                    <div className="profile-dropdown-divider"></div>

                                    {/* Mode Switch */}
                                    <div className="mode-switch-container">
                                        <div className="mode-switch-label">
                                            <LuArrowLeftRight /> {lang === 'tr' ? 'Mod Değiştir' : 'Switch Mode'}
                                        </div>
                                        <div className="mode-switch-btns">
                                            <button
                                                className={`mode-switch-btn ${activeMode === 'crypto' ? 'active' : ''}`}
                                                onClick={() => handleModeSwitch('crypto')}
                                            >
                                                <LuBitcoin />
                                                <span>Kripto</span>
                                            </button>
                                            <button
                                                className={`mode-switch-btn ${activeMode === 'fiat' ? 'active' : ''}`}
                                                onClick={() => handleModeSwitch('fiat')}
                                            >
                                                <LuLandmark />
                                                <span>Fiat</span>
                                            </button>
                                        </div>
                                    </div>

                                    <div className="profile-dropdown-divider"></div>
                                    <button onClick={() => handleMenuItemClick('/profile')}>
                                        <LuUser /> {t('nav.profile')}
                                    </button>
                                    <button onClick={() => handleMenuItemClick('/settings')}>
                                        <LuSettings /> {t('nav.settings')}
                                    </button>
                                    <div className="profile-dropdown-divider"></div>
                                    <div className="profile-dropdown-preferences">
                                        <button onClick={toggleLanguage} className="preference-btn">
                                            <span className="preference-label">{lang === 'tr' ? 'Dil' : 'Language'}</span>
                                            <span className="preference-value">{lang.toUpperCase()}</span>
                                        </button>
                                        <button onClick={toggleTheme} className="preference-btn">
                                            <span className="preference-label">{lang === 'tr' ? 'Tema' : 'Theme'}</span>
                                            <span className="preference-icon">{isDark ? <LuSun /> : <LuMoon />}</span>
                                        </button>
                                    </div>
                                    <div className="profile-dropdown-divider"></div>
                                    <button onClick={handleLogout} className="logout-btn">
                                        <LuLogOut /> {t('auth.logout')}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className="profile-dropdown-guest">
                                        <LuUser className="guest-icon" />
                                        <span>{lang === 'tr' ? 'Misafir' : 'Guest'}</span>
                                    </div>
                                    <div className="profile-dropdown-divider"></div>
                                    <div className="profile-dropdown-preferences">
                                        <button onClick={toggleLanguage} className="preference-btn">
                                            <span className="preference-label">{lang === 'tr' ? 'Dil' : 'Language'}</span>
                                            <span className="preference-value">{lang.toUpperCase()}</span>
                                        </button>
                                        <button onClick={toggleTheme} className="preference-btn">
                                            <span className="preference-label">{lang === 'tr' ? 'Tema' : 'Theme'}</span>
                                            <span className="preference-icon">{isDark ? <LuSun /> : <LuMoon />}</span>
                                        </button>
                                    </div>
                                    <div className="profile-dropdown-divider"></div>
                                    <Link to="/login" className="profile-dropdown-login" onClick={() => setDropdownOpen(false)}>
                                        {t('auth.login')}
                                    </Link>
                                    <Link to="/register" className="profile-dropdown-register" onClick={() => setDropdownOpen(false)}>
                                        {t('auth.register')}
                                    </Link>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </nav>
    );
}

export default Navbar;
